import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Database, Lock, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Confidentialite = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Database,
      title: t('confidentialite.s1.title', "1. Collecte des données"),
      content: t('confidentialite.s1.content', "Nous collectons les informations que vous nous fournissez directement lorsque vous utilisez notre formulaire de contact, notamment votre nom, votre adresse e-mail et le contenu de votre message.")
    },
    {
      icon: ShieldCheck,
      title: t('confidentialite.s2.title', "2. Utilisation des données"),
      content: t('confidentialite.s2.content', "Les informations que nous collectons sont utilisées exclusivement pour :"),
      list: [
        t('confidentialite.s2.l1', "Répondre à vos demandes de renseignements ou de devis"),
        t('confidentialite.s2.l2', "Améliorer notre service client"),
        t('confidentialite.s2.l3', "Vous contacter concernant votre projet")
      ]
    },
    {
      icon: Lock,
      title: t('confidentialite.s3.title', "3. Protection des données"),
      content: t('confidentialite.s3.content', "Nous mettons en œuvre une variété de mesures de sécurité pour préserver la sécurité de vos informations personnelles. Vos données ne seront ni vendues, ni échangées, ni transférées, ni données à une autre société sans votre consentement.")
    },
    {
      icon: Mail,
      title: t('confidentialite.s4.title', "4. Contact"),
      content: t('confidentialite.s4.content', "Pour toute question concernant cette politique de confidentialité, vous pouvez nous contacter à l'adresse :"),
      highlight: "services@auxis.mg"
    }
  ];

  return (
    <div className="min-h-screen bg-auxis-off-white dark:bg-auxis-charcoal py-20 px-4 sm:px-6 lg:px-8 font-quicksand relative overflow-hidden transition-colors duration-300">
      {/* Background with image */}
      <div className="absolute top-0 left-0 w-full h-96 -z-0 overflow-hidden rounded-b-[3rem] md:rounded-b-[5rem]">
        <div className="absolute inset-0 bg-auxis-charcoal" />
      </div>
      <div className="absolute top-20 right-10 w-64 h-64 bg-auxis-gold/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 flex justify-between items-center"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-auxis-gold transition-colors duration-300 font-medium group"
          >
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-auxis-gold/20 transition-colors">
              <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            </div>
            {t('confidentialite.back', "Retour à l'accueil")}
          </Link>

          <button
            onClick={() => i18n.changeLanguage(isEng ? 'fr' : 'en')}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-auxis-gold/20 text-white/80 hover:text-auxis-gold transition-all duration-300 font-medium border border-white/10 hover:border-auxis-gold/30"
          >
            <span className="uppercase text-sm tracking-wider">{isEng ? 'EN' : 'FR'}</span>
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white dark:bg-auxis-charcoal/90 dark:backdrop-blur-xl rounded-3xl shadow-soft-xl p-8 md:p-12 lg:p-16 border border-gray-100 dark:border-white/10"
        >
          <div className="flex items-center gap-4 mb-10 pb-8 border-b border-gray-100 dark:border-white/10">
            <div className="w-14 h-14 rounded-2xl bg-auxis-gold/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-7 h-7 text-auxis-gold" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-auxis-charcoal dark:text-white tracking-tight">
                {t('confidentialite.title', "Politique de Confidentialité")}
              </h1>
              <p className="text-auxis-charcoal/50 dark:text-white/50 mt-2 text-sm font-medium">
                {t('confidentialite.updated', "Dernière mise à jour :")} {new Date().toLocaleDateString(isEng ? 'en-US' : 'fr-FR', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.section 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1 w-8 h-8 rounded-lg bg-gray-50 dark:bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-auxis-gold/10 group-hover:text-auxis-gold transition-colors duration-300">
                    <section.icon size={16} className="text-auxis-charcoal/40 dark:text-white/40 group-hover:text-auxis-gold transition-colors" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-auxis-charcoal dark:text-white mb-3 group-hover:text-auxis-gold transition-colors duration-300">
                      {section.title}
                    </h2>
                    <p className="text-auxis-charcoal/70 dark:text-white/70 leading-relaxed text-lg">
                      {section.content}
                    </p>
                    
                    {section.list && (
                      <ul className="mt-4 space-y-3">
                        {section.list.map((item, i) => (
                          <li key={i} className="flex items-center gap-3 text-auxis-charcoal/70 dark:text-white/70">
                            <span className="w-1.5 h-1.5 rounded-full bg-auxis-gold shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}

                    {section.highlight && (
                      <div className="mt-4 inline-block px-4 py-2 bg-auxis-gold/5 rounded-lg border border-auxis-gold/10">
                        <strong className="text-auxis-gold font-semibold">{section.highlight}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </motion.section>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Confidentialite;